/**
 * The khutbah plan — who speaks, in which language, on what, every Friday.  MKK adoption A1 (module 31), owner 2026-10-24.
 *
 * One row per (day, slot): the Friday — or the Eid day, as a free extra row — the slot (the Jumu'ah, a second Jumu'ah,
 * Eid), the topic, the khatib, the language, a note for staff, and whether the row is shown. Members see it in the app
 * before Friday, the screens show it in the Jumu'ah block, staff and the imam write it from the panel and the phone.
 * Behind the `khutbah_plan` capability and the `khutbah.manage` permission (access.js).
 *
 * Pure: no I/O, no clock. "Today" is the MOSQUE's calendar date (tz.dateIn), never UTC's, and the time of day is the
 * mosque's wall clock: on a Friday the plan is "this Friday's" until Asr begins, then it is next week's — decided here
 * once, so the app, the screens and the website never disagree about which Friday is next.
 */
const Day = require('./day');

const SLOTS = ['jumua', 'jumua2', 'eid'];
const SLOT_NO = { jumua: 'Jumu\'ah', jumua2: 'Jumu\'ah 2', eid: 'Eid' };
const MAX_TEXT = 120;
const MAX_NOTES = 1000;
/** What a row carries; `notes` is staff-only and never in a public or member view. */
const FIELDS = ['day', 'slot', 'topic', 'khatib', 'language', 'notes', 'visible'];
const PUBLIC_FIELDS = ['day', 'slot', 'topic', 'khatib', 'language'];
/** The next Friday and up to four following: what members and screens are shown. */
const FRIDAYS_AHEAD = 5;
/** The Fridays the panel and the phone prefill for staff to fill in. */
const PANEL_FRIDAYS = 8;
const CONTROL = /[\u0000-\u0008\u000b-\u001f\u007f]/;   // tab and newline are text; the rest is not
const isObject = (v) => Boolean(v) && typeof v === 'object' && !Array.isArray(v);
const isFriday = (day) => Day.valid(day) && Day.weekday(day) === 5;

/** One line of text, optional: whitespace runs collapse, at most `max`, no control characters. '' and null are null. */
function oneLine(v, label, max) {
  if (v === undefined || v === null) return { ok: true, value: null };
  if (typeof v !== 'string') return { ok: false, message: `${label} må være tekst.` };
  const s = v.replace(/\s+/g, ' ').trim();
  if (!s) return { ok: true, value: null };
  if (s.length > max) return { ok: false, message: `${label} kan ha maks ${max} tegn.` };
  if (CONTROL.test(s)) return { ok: false, message: `${label} kan ikke ha kontrolltegn.` };
  return { ok: true, value: s };
}
/** Several lines, optional: trimmed, at most `max`, no control characters but tab and newline. */
function text(v, label, max) {
  if (v === undefined || v === null) return { ok: true, value: null };
  if (typeof v !== 'string') return { ok: false, message: `${label} må være tekst.` };
  const s = v.replace(/\r\n?/g, '\n').trim();
  if (!s) return { ok: true, value: null };
  if (s.length > max) return { ok: false, message: `${label} kan ha maks ${max} tegn.` };
  if (CONTROL.test(s)) return { ok: false, message: `${label} kan ikke ha kontrolltegn.` };
  return { ok: true, value: s };
}

/**
 * A row as staff send it — every field checked, unknown fields refused. The slot is the Jumu'ah unless said; a row is
 * shown unless said. A row with no topic, no khatib and no language is nothing to show and nothing to keep: refused,
 * so an empty prefilled line in the panel is never saved by mistake (delete removes a row).
 * → { ok, value: {day, slot, topic, khatib, language, notes, visible} } | { ok: false, errors }
 */
function input(body) {
  if (!isObject(body)) return { ok: false, errors: { khutbah: 'Ugyldig khutbah-plan.' } };
  const unknown = Object.keys(body).filter((k) => !FIELDS.includes(k));
  if (unknown.length) return { ok: false, errors: Object.fromEntries(unknown.map((k) => [k, 'Ukjent felt.'])) };
  const errors = {};
  const out = {};
  const take = (k, r) => { if (r.ok) out[k] = r.value; else errors[k] = r.message; };
  if (!Day.valid(body.day)) errors.day = 'Dagen må være en gyldig dato (ÅÅÅÅ-MM-DD).'; else out.day = body.day;
  const slot = body.slot === undefined || body.slot === null || body.slot === '' ? 'jumua' : body.slot;
  if (!SLOTS.includes(slot)) errors.slot = 'Velg Jumu\'ah, Jumu\'ah 2 eller Eid.'; else out.slot = slot;
  take('topic', oneLine(body.topic, 'Temaet', MAX_TEXT));
  take('khatib', oneLine(body.khatib, 'Khatib', MAX_TEXT));
  take('language', oneLine(body.language, 'Språket', MAX_TEXT));
  take('notes', text(body.notes, 'Notatet', MAX_NOTES));
  const visible = body.visible === undefined || body.visible === null ? true : body.visible;
  if (visible === true || visible === 1) out.visible = true;
  else if (visible === false || visible === 0) out.visible = false;
  else errors.visible = '«Vis i appen» er ja eller nei.';
  if (!errors.topic && !errors.khatib && !errors.language && !out.topic && !out.khatib && !out.language) {
    errors.topic = 'Skriv minst tema, khatib eller språk.';
  }
  if (Object.keys(errors).length) return { ok: false, errors };
  return { ok: true, value: out };
}

/** The `count` Fridays from `from` on, `from` itself included when it is one. */
function fridays(from, count = FRIDAYS_AHEAD) {
  if (!Day.valid(from) || !(count > 0)) return [];
  const first = Day.add(from, (5 - Day.weekday(from) + 7) % 7);
  return Array.from({ length: count }, (_, i) => Day.add(first, 7 * i));
}

/**
 * Whether today's Jumu'ah is still to come: before Asr begins, by the mosque's clock. `nowHm` and `asrBegins` are
 * 'HH:MM'; without a timetable (no Asr known) the whole day counts, so a plan is never hidden early for lack of a time.
 */
function stillToday(nowHm, asrBegins) {
  if (!/^\d{2}:\d{2}$/.test(String(asrBegins || ''))) return true;
  return String(nowHm || '00:00') < asrBegins;
}

/**
 * The Friday the public and the members are shown: today when it is Friday and its Jumu'ah is still to come, else
 * the coming one. And the window the public view spans: from today (or tomorrow, once today's is over) to the fifth
 * Friday, so an Eid row in between is shown too.
 */
function window(today, { nowHm = '00:00', asrBegins = null } = {}) {
  if (!Day.valid(today)) return null;
  const todayCounts = stillToday(nowHm, asrBegins);
  const from = todayCounts ? today : Day.add(today, 1);
  const list = fridays(from, FRIDAYS_AHEAD);
  return { from, to: list[list.length - 1], fridays: list, nextFriday: list[0] };
}

/** One row as the public sees it: the five public fields, never the note and never who wrote it. */
function publicRow(r) {
  return { day: r.day, slot: r.slot, topic: r.topic ?? null, khatib: r.khatib ?? null, language: r.language ?? null };
}

const SLOT_RANK = Object.fromEntries(SLOTS.map((s, i) => [s, i]));
/**
 * The public and member view: visible rows inside the window, oldest first, in slot order within a day, without notes.
 * Past rows are staff history and are not here, whatever `visible` says.
 */
function publicRows(rows, today, opts = {}) {
  const w = window(today, opts);
  if (!w) return [];
  return (rows || [])
    .filter((r) => r && (r.visible === true || r.visible === 1) && Day.valid(r.day) && r.day >= w.from && r.day <= w.to && SLOTS.includes(r.slot))
    .sort((a, b) => a.day.localeCompare(b.day) || SLOT_RANK[a.slot] - SLOT_RANK[b.slot])
    .map(publicRow);
}

/** The one line a card or a screen prints: the parts that are set, joined with a middle dot; null when none. */
function line(r) {
  if (!r) return null;
  const parts = [r.topic, r.khatib, r.language].map((x) => (typeof x === 'string' ? x.trim() : '')).filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

/** What the activity log keeps: the day, the slot and the switch. Never the note, never the topic. */
function audit(r) {
  return r ? { khutbah_day: r.day, khutbah_slot: r.slot, khutbah_visible: r.visible === true || r.visible === 1 ? 1 : 0 } : null;
}

module.exports = { SLOTS, SLOT_NO, MAX_TEXT, MAX_NOTES, FIELDS, PUBLIC_FIELDS, FRIDAYS_AHEAD, PANEL_FRIDAYS,
  isFriday, input, fridays, stillToday, window, publicRow, publicRows, line, audit };
